import { useState, FormEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../App'
import { api } from '../services/api'

// Helper: ArrayBuffer to base64url
function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

// Helper: base64url to ArrayBuffer
function base64urlToBuffer(base64url: string): ArrayBuffer {
  let s = base64url.replace(/-/g, '+').replace(/_/g, '/')
  while (s.length % 4) s += '='
  const binary = atob(s)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

export default function LoginPage() {
  const [username, setUsername] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const { login } = useAuth()

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!username.trim()) {
      setError('Please enter your username')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await api.login(username.trim())
      const userId = response.user?.id || username.trim()
      login(userId, response.user?.username || username.trim(), response.token)

      if (response.user?.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(response.user.id)) {
        localStorage.setItem('lynkstar_my_uuid', response.user.id)
      }

      navigate('/chats')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handlePasskeyLogin = async () => {
    setIsPasskeyLoading(true)
    setError(null)

    try {
      // Check WebAuthn support
      if (!window.PublicKeyCredential) {
        setError('Your browser does not support Passkeys')
        return
      }

      // 1. Get login options from server
      const { options } = await api.getPasskeyLoginOptions(username.trim() || undefined)

      // 2. Convert challenge and credential IDs from base64url to ArrayBuffer
      const publicKeyOptions: CredentialRequestOptions = {
        publicKey: {
          challenge: base64urlToBuffer(options.challenge),
          rpId: options.rpId,
          timeout: options.timeout,
          userVerification: options.userVerification as UserVerificationRequirement,
          allowCredentials: options.allowCredentials.map((cred: any) => ({
            id: base64urlToBuffer(cred.id),
            type: cred.type,
            ...(cred.transports ? { transports: cred.transports } : {}),
          })),
        },
      }

      // 3. Call navigator.credentials.get()
      const credential = await navigator.credentials.get(publicKeyOptions) as PublicKeyCredential | null
      if (!credential) {
        setError('Passkey authentication was cancelled')
        return
      }

      const assertionResponse = credential.response as AuthenticatorAssertionResponse

      // 4. Send to server for verification
      const response = await api.verifyPasskeyLogin({
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64url(assertionResponse.clientDataJSON),
          authenticatorData: bufferToBase64url(assertionResponse.authenticatorData),
          signature: bufferToBase64url(assertionResponse.signature),
          userHandle: assertionResponse.userHandle
            ? bufferToBase64url(assertionResponse.userHandle)
            : undefined,
        },
      })

      // 5. Login successful
      const userId = response.user?.id || ''
      login(userId, response.user?.username || '', response.token)

      if (response.user?.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(response.user.id)) {
        localStorage.setItem('lynkstar_my_uuid', response.user.id)
      }

      navigate('/chats')
    } catch (err) {
      console.error('Passkey login error:', err)
      if (err instanceof Error && err.name === 'NotAllowedError') {
        setError('Passkey authentication was cancelled or timed out')
      } else {
        setError(err instanceof Error ? err.message : 'Passkey login failed')
      }
    } finally {
      setIsPasskeyLoading(false)
    }
  }

  const isAnyLoading = isLoading || isPasskeyLoading

  return (
    <div className="min-h-screen flex flex-col justify-center px-6 py-12">
      <div className="mx-auto w-full max-w-sm">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="w-20 h-20 bg-primary rounded-2xl flex items-center justify-center shadow-lg">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-10 w-10 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Welcome Back
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mb-8">
          Sign in to continue with end-to-end encrypted messaging
        </p>

        {/* Passkey Login Button */}
        <button
          onClick={handlePasskeyLogin}
          disabled={isAnyLoading}
          className="w-full flex items-center justify-center gap-3 px-4 py-3 mb-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl font-medium hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors disabled:opacity-50"
        >
          {isPasskeyLoading ? (
            <div className="spinner" />
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
              </svg>
              Sign in with Passkey
            </>
          )}
        </button>

        {/* Divider */}
        <div className="relative mb-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300 dark:border-gray-600" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-3 bg-white dark:bg-dark-bg text-gray-500 dark:text-gray-400">
              or sign in with username
            </span>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Username Input */}
          <div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username"
                className="input-field pl-12"
                disabled={isAnyLoading}
                autoComplete="username webauthn"
                autoFocus
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-error/10 text-error text-sm px-4 py-3 rounded-xl">
              {error}
            </div>
          )}

          {/* Security Notice */}
          <div className="security-badge w-full justify-start">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
            <span className="text-left">
              Use Passkey for the most secure login. Your biometric data never leaves your device.
            </span>
          </div>

          {/* Login Button */}
          <button
            type="submit"
            disabled={isAnyLoading}
            className="btn-primary w-full flex items-center justify-center"
          >
            {isLoading ? (
              <div className="spinner" />
            ) : (
              'Sign In with Username'
            )}
          </button>
        </form>

        {/* Register Link */}
        <div className="mt-8 text-center">
          <span className="text-gray-500 dark:text-gray-400">
            Don't have an account?{' '}
          </span>
          <Link
            to="/register"
            className="text-primary font-medium hover:underline"
          >
            Create Account
          </Link>
        </div>

        {/* Brand */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-400 dark:text-gray-500">
            Powered by <span className="font-semibold text-primary">LYNKSTAR</span>
          </p>
        </div>
      </div>
    </div>
  )
}
